import { Typography } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import SearchBar from '../../SearchBar/SearchBar';
import { CustomListContainer, CustomListEntryContainer } from '../../CustomList/CustomList.styles';
import PeopleListEntry from './PeopleListEntry/PeopleListEntry';
import UserAccountSection from './UserAccountSection/UserAccountSection';
import { PageHeader } from './EmployeeDirectory.styles';
import { fetchFutureShifts } from '../../../redux/actions/shiftActions';
import { DRAWER_TYPES } from '../../../pages/People/peopleDrawerConfig';
import { setSelectedEmployee, selectSelectedEmployee } from '../../../redux/people/peopleSlice';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import { getEmployeesStatus, processEmployeeStatusData } from '../../../utils/statusUtils';
import StatusFilter from './StatusFilter/StatusFilter';
import PeopleTableHeader from './PeopleTableHeader/PeopleTableHeader';

const EmployeeDirectory = ({ handleOpenDrawer }) => {
  const { personId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch shift data when the component mounts
  useEffect(() => {
    dispatch(fetchFutureShifts());
  }, [dispatch]);

  // Get data from Redux store
  const employees = useSelector((state) => state.employees.employees);
  const currentUser = useSelector((state) => state.currentUser);
  const currentVenue = useSelector(selectCurrentVenue);
  const futureShifts = useSelector((state) => state.futureShifts.shifts);
  const selectedUser = useSelector(selectSelectedEmployee);
  const currentEmployee = useMemo(() => employees.find((emp) => emp.id === personId), [employees, personId]);

  // State variables
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeStatusData, setEmployeeStatusData] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch selected person from params and current state
  useEffect(() => {
    if (selectedUser?.id !== personId) {
      if (currentEmployee) {
        const drawerType = personId === currentUser.employeeInfo.id ? DRAWER_TYPES.USER : DRAWER_TYPES.EMPLOYEE;

        dispatch(setSelectedEmployee(currentEmployee));
        handleOpenDrawer(drawerType, currentEmployee);
      } else {
        dispatch(setSelectedEmployee(null));
      }
    }
  }, [dispatch, personId, employees, currentEmployee, currentUser.employeeInfo.id, selectedUser?.id, handleOpenDrawer]);

  // Fetch employee status data
  useEffect(() => {
    const fetchEmployeeStatus = async () => {
      if (employees.length === 0 || !currentVenue?.id) {
        return;
      }

      try {
        const employeeIds = employees.map((emp) => emp.id);
        const statusResponse = await getEmployeesStatus(currentVenue.id, employeeIds);
        const processedData = processEmployeeStatusData(statusResponse);

        // Convert array to map for easy lookup
        const statusMap = {};
        processedData.employees.forEach((emp) => {
          statusMap[emp.employeeId] = emp;
        });

        setEmployeeStatusData(statusMap);
      } catch (error) {
        console.error('Failed to fetch employee status:', error);
      }
    };

    fetchEmployeeStatus();

    // Set up interval to refresh status every 30 seconds
    const interval = setInterval(fetchEmployeeStatus, 30000);

    return () => clearInterval(interval);
  }, [employees, currentVenue]);

  // Event handlers
  const handleEmployeeClick = (employee) => {
    navigate(`/people/${employee.id}`);
  };

  const handleUserAccountClick = () => {
    navigate(`/people/${currentUser.employeeInfo.id}`);
  };

  // Filter and sort employees by name and status
  const filteredEmployees = employees
    .filter((employee) => {
      const matchesSearch = employee.displayName.toLowerCase().includes(searchTerm);
      const isActive = employee.isActive === true;

      if (!matchesSearch || !isActive) return false;

      if (statusFilter === 'all') return true;

      const employeeStatus = employeeStatusData[employee.id];
      const statusesToCheck = employeeStatus?.allStatuses || ['clocked-out'];
      return statusesToCheck.includes(statusFilter);
    })
    .sort((a, b) => {
      // Sort by status priority (urgent statuses first)
      const statusA = employeeStatusData[a.id];
      const statusB = employeeStatusData[b.id];

      const priorityA = statusA?.priority || 10;
      const priorityB = statusB?.priority || 10;

      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Lower priority number = higher urgency
      }

      // If same priority, sort alphabetically
      return a.displayName.localeCompare(b.displayName);
    });

  // Calculate status counts for filter - now includes all applicable statuses
  const statusCounts = useMemo(() => {
    const counts = {};
    employees.forEach((employee) => {
      if (!employee.isActive) return;

      const employeeStatus = employeeStatusData[employee.id];
      const statusesToCount = employeeStatus?.allStatuses || ['clocked-out'];

      statusesToCount.forEach((status) => {
        counts[status] = (counts[status] || 0) + 1;
      });
    });
    return counts;
  }, [employees, employeeStatusData]);

  // Function to get future shifts for an employee
  const getFutureShiftCountForEmployee = (eid) => {
    return futureShifts.filter((shift) => shift.eid === eid).length;
  };

  return (
    <>
      <PageHeader>
        <Typography variant="h3">People</Typography>
      </PageHeader>

      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {/* Status Filter */}
      <StatusFilter selectedFilter={statusFilter} onFilterChange={setStatusFilter} statusCounts={statusCounts} />

      {/* Active User account */}
      <Typography variant="h6GrayUppercase">Your Account</Typography>
      <UserAccountSection
        user={currentUser}
        futureShiftCount={getFutureShiftCountForEmployee(currentUser.employeeInfo.id)}
        onClick={() => handleUserAccountClick()}
        handleOpenDrawer={handleOpenDrawer}
      />
      {/* Employee Directory */}
      <PeopleTableHeader />
      <CustomListContainer>
        {filteredEmployees.map((employee, index) => (
          <CustomListEntryContainer key={`${employee.eid}-${index}`} onClick={() => handleEmployeeClick(employee)}>
            <PeopleListEntry
              employee={employee}
              isLast={index === filteredEmployees.length - 1}
              futureShiftCount={getFutureShiftCountForEmployee(employee.id)}
              statusData={employeeStatusData[employee.id]}
            />
          </CustomListEntryContainer>
        ))}
      </CustomListContainer>
    </>
  );
};

export default EmployeeDirectory;
